import { createContext, useContext } from 'react';
import {
  NormalHoursType,
  useNormalHours,
} from './normal-hours-calendar/normal-hours.hooks';
import {
  ClosedDatesType,
  SideCalendarType,
  StandardHoursType,
  TemporaryHoursType,
  useTemporaryHours,
} from './temporary-hours-calendar/temporary-hours.hooks';
import { StoreOrderingForm } from 'api/ordering-service/ordering-service.types';

export type LocationHoursContextType = {
  normalHours: NormalHoursType;
  temporaryHours: TemporaryHoursType;
  closedDates: ClosedDatesType;
  sideCalendar: SideCalendarType;
  standardHours: StandardHoursType;
  getUpdatedLocationHoursData: () => Partial<StoreOrderingForm>;

  // TODO kitchenCapacity: {}

  // Call this method when you're actually ready to save. It will give you
  // all of the hours-related stuff in the ordering object
};

export const LocationHoursContext = createContext<
  LocationHoursContextType | undefined
>(undefined);

export const useLocationHoursContext = () => {
  const context = useContext(LocationHoursContext);

  /* istanbul ignore next */
  if (!context)
    throw new Error(
      'useLocationHoursContext must be used within a LocationHoursContextProvider',
    );

  return context;
};

type LocationHoursProviderType = {
  children: React.ReactNode;
  storeOrderingData: StoreOrderingForm;
};

const LocationHoursProvider = ({
  children,
  storeOrderingData,
}: LocationHoursProviderType) => {
  const {
    pickupTemporaryHours,
    deliveryTemporaryHours,
    pickupOffDays,
    deliveryOffDays,
  } = storeOrderingData;

  const normalHours = useNormalHours({ storeOrderingData });
  const { normalEvents, convertEventsToNormalHours } = normalHours;

  const { temporaryHours, closedDates, sideCalendar, standardHours } =
    useTemporaryHours({
      storeOrderingData,
      normalEvents,
    });
  const {
    backgroundEvents,
    convertEventsToTemporaryHours,
    temporaryHoursCalendarLoaded,
  } = temporaryHours;

  const { convertEventsToClosedDates } = closedDates;

  const getUpdatedLocationHoursData = () => {
    const normalCalendarEvents = normalEvents
      ? convertEventsToNormalHours(normalEvents)
      : convertEventsToNormalHours(backgroundEvents);

    const temporaryCalendarEvents = temporaryHoursCalendarLoaded
      ? { ...convertEventsToTemporaryHours(), ...convertEventsToClosedDates() }
      : {
          pickupTemporaryHours,
          deliveryTemporaryHours,
          pickupOffDays,
          deliveryOffDays,
        };

    return {
      ...normalCalendarEvents,
      ...temporaryCalendarEvents,
    };
  };

  const locationHoursContextValue = {
    normalHours,
    temporaryHours,
    closedDates,
    sideCalendar,
    standardHours,
    getUpdatedLocationHoursData,
  };

  return (
    <LocationHoursContext.Provider value={locationHoursContextValue}>
      {children}
    </LocationHoursContext.Provider>
  );
};

export default LocationHoursProvider;
